import { createSlice, createAction } from "@reduxjs/toolkit";
import listFactory from "./listFactory";
import { UUID } from "../utils";
import { find } from "lodash";
import Customer from "../models/Customer";
import CustomerPreferencesBranding from "../models/CustomerPreferencesBranding";
import { CustomerLogoType } from "../models/CustomerLogo";
import CustomerAddress from "../models/CustomerAddress";
const { slicePart, selectors } = listFactory<Customer>("customers");

export const initialState = {
  ...slicePart.initialState,
  topLevelCustomer: {
    registeredBusinessName: "",
    countryOfRegistration: "",
    taxId: "",
    registeredAddress: {
      streetAddress1: "",
      streetAddress2: "",
      city: "",
      state: "",
      postcode: "",
      country: "",
    },
  },
  banksByCountry: [],
  banksCountByCountry: [],
  customerPreferencesBranding: null,
};

export const slice = createSlice({
  ...slicePart,
  initialState,
  reducers: {
    setItem: slicePart.reducers.setItem,
    setList: slicePart.reducers.setList,
    addItem: slicePart.reducers.addItem,
    updateItem: slicePart.reducers.updateItem,
    setTopLevelCustomer: (state, action) => {
      state.topLevelCustomer = action.payload;
    },
    clearTopLevelCustomer: (state) => {
      state.topLevelCustomer = initialState.topLevelCustomer;
    },
    setCustomerCredentials: (state, action) => {
      const { id, credentials } = action.payload;
      const item = find(state.list, { id });
      if (item) {
        Object.assign(item, {
          credentials: {
            ...item.credentials,
            ...credentials,
          },
        });
      }
    },
    clearCustomerCredentials: (state, action) => {
      const { id } = action.payload;
      const item = find(state.list, { id });
      if (item) {
        Object.assign(item, {
          credentials: {
            ...item.credentials,
            password: "",
          },
        });
      }
    },
    setCustomerBanksByCountry: (state, action) => {
      state.banksByCountry = action.payload;
    },
    setCustomerBanksCountByCountry: (state, action) => {
      state.banksCountByCountry = action.payload;
    },
    setCustomerPreferencesBranding: (state, action) => {
      state.customerPreferencesBranding = action.payload;
    },
    updateCustomerAddress: (state, action) => {
      const { id, address } = action.payload;
      const item = find(state.list, { id });
      Object.assign(item, { address: address });
    },
  },
});

export const {
  setList,
  setItem,
  addItem,
  updateItem,
  setTopLevelCustomer,
  clearTopLevelCustomer,
  setCustomerCredentials,
  clearCustomerCredentials,
  setCustomerBanksByCountry,
  setCustomerBanksCountByCountry,
  setCustomerPreferencesBranding,
  updateCustomerAddress: setCustomerAddress,
} = slice.actions;

export const fetchCustomers = createAction("fetchCustomers");
export const fetchCustomer = createAction<{ id: UUID }, "fetchCustomer">(
  "fetchCustomer"
);

export const createCustomer = createAction<{ data: any }>("createCustomer");
export const createCustomerAddress = createAction<{
  id: UUID;
  data: CustomerAddress;
}>("createCustomerAddress");

export const updateCustomerAddress = createAction<{
  id: UUID;
  data: CustomerAddress;
}>("updateCustomerAddress");

export const updateCustomer = createAction<{ id: UUID; data: any }>(
  "updateCustomer"
);

export const updateCustomerBanksByCountry = createAction<{
  id: UUID;
  countries: string[];
}>("updateCustomerBanksByCountry");
export const fetchCustomersBanksByCountry = createAction<{ id: UUID }>(
  "fetchCustomersBanksByCountry"
);
export const fetchCustomersBanksCountByCountry = createAction<{ id: UUID }>(
  "fetchCustomersBanksCountByCountry"
);
export const setCustomerActiveFlag = createAction<{ id: UUID; value: boolean }>(
  "setCustomerActiveFlag"
);
export const fetchTopLevelCustomer = createAction("fetchTopLevelCustomer");
export const generateCustomerCredentials = createAction<{ id: UUID }>(
  "generateCustomerCredentials"
);
export const regenerateCustomerCredentials = createAction<{ id: UUID }>(
  "regenerateCustomerCredentials"
);

export const fetchCustomerPreferencesBranding = createAction<{ id: UUID }>(
  "fetchCustomerPreferencesBranding"
);

export const updateCustomerPreferencesBranding = createAction<{
  id: UUID;
  data: CustomerPreferencesBranding;
}>("updateCustomerPreferencesBranding");

export const updateCustomerLogo = createAction<{
  id: UUID;
  logoType: CustomerLogoType;
  file: File;
}>("updateCustomerLogo");

export const deleteCustomerLogo = createAction<{
  id: UUID;
  logoType: CustomerLogoType;
}>("deleteCustomerLogo");

export const {
  selectList: selectCustomers,
  selectItem: selectCustomer,
} = selectors;

export const selectTopLevelCustomer = (state: any) =>
  state.customers.topLevelCustomer;
export const selectCustomerCredentials = (state: any) =>
  state.customers.credentials;
export const selectCustomerBanksByCountry = (state: any) =>
  state.customers.banksByCountry;
export const selectCustomerBanksCountByCountry = (state: any) =>
  state.customers.banksCountByCountry;
export const selectCustomerPreferencesBranding = (state: any) =>
  state.customers.customerPreferencesBranding;
export const selectCustomerAddress = (id: UUID) => (state: any) =>
  find(state.customers.list, { id })?.address;

export const customerAddressOperationPaths = (id?: UUID) => {
  return {
    update: ["customerAddress", "update", id],
    create: ["customerAddress", "create", id],
  };
};

export const customerOperationPaths = (id?: UUID) => {
  return {
    listing: ["customers.list"],
    details: ["customers", "items", "details", id],
    update: ["customers", "items", "update", id],
    create: ["customers", "create"],
    setActiveFlag: ["customers", "items", id, "setCustomerActiveFlag"],
  };
};

export default slice.reducer;
